// =============================================================================
// Order Deadline app - cart drawer integration + delivery-date reliability fix
// BushWear, 25 Sep 2026
//
// WHY THIS FILE CHANGED
// The Order Deadline app writes the delivery dates to the cart with several
// requests fired at the same moment: one /cart/update.js (order-level
// orderDeadlineApp* attributes) plus one /cart/change.js per line
// (expected_ship_date / expected_arrival_date). Shopify's cart does not merge
// simultaneous writes - each one overwrites the others - so some of them are
// silently lost. The order then arrives with no date on some lines, no
// order-level message, or both. Roughly 1 in 6 web orders was affected.
//
// FIX
// 1. Cart writes (POST /cart/add, /cart/change, /cart/update) are queued and
//    sent one at a time, so none can overwrite another.
// 2. The Checkout button waits (max ~6s) until every line carries its date.
// 3. Express buttons (Apple Pay / Google Pay / PayPal) in the drawer and on the
//    cart page are paused for the same short window.
// 4. If the drawer opens before the app has loaded, we retry instead of
//    giving up (previously the drawer never ran the app in that case).
//
// 3 Nov 2026: the app's date keys are hidden (_expected_ship_date /
// _expected_arrival_date). Both old and hidden keys are accepted here. When
// the app writes a line's dates, a customer-facing "Ready to ship: Tue 3 Nov"
// property is added to the same request, so it is not a separate cart write.
// The AWS Lambda updatePaidOrdersInRO reads the date keys to fill RetailOps.
//
// 3 Nov 2026: the drawer is drawn before the app writes the dates, so a new
// line showed no date until the drawer was reopened. Once dates land, the
// drawer is redrawn once if the cart holds more dates than it is showing.
// =============================================================================

(function () {
  if (window.__bwCartSerial) { return; }
  window.__bwCartSerial = true;

  // ---------------------------------------------------------------------------
  // 1. Serialise cart writes
  // ---------------------------------------------------------------------------
  var CART_WRITE = /\/cart\/(change|update|add)(\.js)?(\?|$)/;
  var queue = Promise.resolve();
  var STEP_TIMEOUT = 10000; // never let one stuck request block the queue

  function enqueue(run) {
    var p = queue.then(function () {
      return new Promise(function (resolve) {
        var finished = false;
        var done = function () { if (!finished) { finished = true; resolve(); } };
        setTimeout(done, STEP_TIMEOUT);
        try { run(done); } catch (e) { done(); }
      });
    });
    queue = p;
    return p;
  }

  // Adds the tidy customer-facing date to the app's own line write. Any problem
  // here returns the request unchanged, so the dates themselves are never lost.
  var BW_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var BW_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function addReadyDate(body) {
    try {
      if (typeof body !== 'string') { return body; }
      var params = new URLSearchParams(body);
      var iso = params.get('properties[_expected_ship_date]') || params.get('properties[expected_ship_date]');
      var m = iso && /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
      if (!m) { return body; }
      var d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
      var label = BW_DAYS[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + BW_MONTHS[d.getUTCMonth()];
      return body + '&' + encodeURIComponent('properties[Ready to ship]') + '=' + encodeURIComponent(label);
    } catch (e) {
      return body;
    }
  }

  var xhrOpen = XMLHttpRequest.prototype.open;
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__bwMethod = method;
    this.__bwUrl = String(url || '');
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    var xhr = this;
    if (/post/i.test(xhr.__bwMethod || '') && CART_WRITE.test(xhr.__bwUrl)) {
      var sendBody = addReadyDate(body);
      enqueue(function (done) {
        xhr.addEventListener('loadend', done);
        xhrSend.call(xhr, sendBody);
      });
      return;
    }
    return xhrSend.apply(xhr, arguments);
  };

  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = (init && init.method) || (input && input.method) || 'GET';
      if (/post/i.test(method) && CART_WRITE.test(url)) {
        var args = arguments, self = this;
        return new Promise(function (resolve, reject) {
          enqueue(function (done) {
            nativeFetch.apply(self, args).then(
              function (r) { done(); resolve(r); },
              function (e) { done(); reject(e); }
            );
          });
        });
      }
      return nativeFetch.apply(this, arguments);
    };
    window.__bwNativeFetch = nativeFetch;
  }

  // ---------------------------------------------------------------------------
  // 2 + 3. Hold checkout until every line has its delivery date
  // ---------------------------------------------------------------------------
  var MAX_WAIT = 6000;
  var getJSON = function (url) {
    return (window.__bwNativeFetch || window.fetch)(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  };

  function datesComplete(cart) {
    if (!cart || !cart.items || cart.items.length === 0) { return true; }
    for (var i = 0; i < cart.items.length; i++) {
      var p = cart.items[i].properties || {};
      if (!p.expected_ship_date && !p._expected_ship_date) { return false; }
    }
    return true;
  }

  // Resolves when writes are finished and dates are on every line, or after MAX_WAIT.
  function waitForDates() {
    var start = Date.now();
    return new Promise(function (resolve) {
      (function check() {
        queue.then(function () { return getJSON('/cart.js'); })
          .then(function (cart) {
            if (datesComplete(cart) || Date.now() - start > MAX_WAIT) { resolve(); }
            else { setTimeout(check, 400); }
          })
          .catch(function () { resolve(); }); // never block checkout on an error
      })();
    });
  }

  // Express buttons: paused while dates are being written
  var style = document.createElement('style');
  style.textContent =
    '.bw-dates-pending .additional-checkout-buttons{opacity:.45;pointer-events:none;transition:opacity .2s}' +
    '.bw-dates-pending .cart__checkout{opacity:.7}';
  (document.head || document.documentElement).appendChild(style);

  var pendingToken = 0;
  function markPending() {
    var token = ++pendingToken;
    document.documentElement.classList.add('bw-dates-pending');
    waitForDates().then(function () {
      if (token === pendingToken) {
        document.documentElement.classList.remove('bw-dates-pending');
      }
    });
  }

  // Checkout buttons (drawer + cart page)
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('button[name="checkout"], input[name="checkout"]');
    if (!btn) { return; }
    if (btn.__bwReady) { btn.__bwReady = false; return; } // second pass: let it through
    e.preventDefault();
    e.stopImmediatePropagation();
    var label = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = 'Confirming delivery dates…';
    waitForDates().then(function () {
      btn.disabled = false;
      btn.innerHTML = label;
      btn.__bwReady = true;
      btn.click();
    });
  }, true);

  var isCartPage = /^\/cart\/?$/.test(window.location.pathname);
  if (isCartPage) {
    document.addEventListener('DOMContentLoaded', markPending);
  }
})();

// =============================================================================
// Order Deadline app - cart drawer integration (original, with load retry)
// - Listen to when cart drawer has been built.
// 1. Insert an app placeholder/loading element within the cart drawer.
// 2. Trigger app's initFromViewCartPage method.
// =============================================================================
(function () {

  // Configuration variables
  var configCartDrawerRootSelector = '#CartDrawerForm .drawer__inner .drawer__footer';
  var configCartDrawerPlacementTargetSelector = '#minicartsub';
  var configCartDrawerPlacementRelativity = 'before';
  var configCartDrawerHideEventName = 'cart:close';
  var configCartDrawerBuiltEventName = 'drawerOpen';

  var cachedInstallConfig = null;
  var cachedTimerWrap = null;
  var cachedCssIsLoaded = null;
  var cartCssIsLoaded = false;

  document.addEventListener(configCartDrawerHideEventName, function () {
    if (!window.orderDeadlineAppByEESL) { return; }
    if (cachedInstallConfig) {
      window.orderDeadlineAppByEESL.installConfig = cachedInstallConfig;
      cachedInstallConfig = null;
    }
    if (cachedTimerWrap) {
      window.orderDeadlineAppByEESL.timerWrap = cachedTimerWrap;
      cachedTimerWrap = null;
    }
    if (cachedCssIsLoaded !== null) {
      window.orderDeadlineAppByEESL.cssIsLoaded = cachedCssIsLoaded;
    }
  });

  function initInDrawer(attempt) {
    // App not loaded yet: retry for up to ~10s instead of giving up
    if (!window.orderDeadlineAppByEESL || !window.orderDeadlineAppByEESL.hasLoaded) {
      if (attempt < 20) { setTimeout(function () { initInDrawer(attempt + 1); }, 500); }
      else { console.log('#OD: app did not load; drawer init skipped.'); }
      return;
    }

    var cartDrawerRoot = document.querySelector(configCartDrawerRootSelector);
    if (!cartDrawerRoot) { console.log('#OD: No cartDrawerRoot found.'); return; }

    var placementTarget = cartDrawerRoot.querySelector(configCartDrawerPlacementTargetSelector);
    if (!placementTarget) { console.log('#OD: No placementTarget found.'); return; }

    if (!cachedInstallConfig && window.orderDeadlineAppByEESL.installConfig) {
      cachedInstallConfig = window.orderDeadlineAppByEESL.installConfig;
    }
    if (!cachedTimerWrap && window.orderDeadlineAppByEESL.timerWrap) {
      cachedTimerWrap = window.orderDeadlineAppByEESL.timerWrap;
    }
    if (!cartCssIsLoaded) {
      cachedCssIsLoaded = window.orderDeadlineAppByEESL.cssIsLoaded;
      window.orderDeadlineAppByEESL.cssIsLoaded = false;
      cartCssIsLoaded = true;
    }

    var existing = cartDrawerRoot.querySelector('.trustblox-1-viewcart-wrap');
    if (existing && existing.parentNode) { existing.parentNode.removeChild(existing); }

    var appLoadingElement = document.createElement('div');
    appLoadingElement.classList.add('trustblox-1-viewcart-wrap');
    if (configCartDrawerPlacementRelativity == 'before') {
      cartDrawerRoot.insertBefore(appLoadingElement, placementTarget);
    } else {
      cartDrawerRoot.insertBefore(appLoadingElement, placementTarget.nextSibling);
    }

    window.orderDeadlineAppByEESL.initFromViewCartPage();
  }

  document.addEventListener(configCartDrawerBuiltEventName, function () {
    document.documentElement.classList.add('bw-dates-pending');
    initInDrawer(0);
    // release express buttons once dates land (or after the max wait)
    setTimeout(function () {
      var ev = new CustomEvent('bw:checkDates');
      document.dispatchEvent(ev);
    }, 0);
  });

  // The drawer is drawn before the app writes its dates. Redraw it once if the
  // cart holds more tidy dates than the drawer is showing. cart:build is the
  // theme's own redraw; it does not fire drawerOpen, so this cannot loop.
  function redrawDrawerIfBehind(cart) {
    var drawer = document.querySelector('#CartDrawerForm');
    if (!drawer || !cart || !cart.items) { return; }
    var inCart = cart.items.filter(function (i) { return i.properties && i.properties['Ready to ship']; }).length;
    var shown = Array.prototype.filter.call(drawer.querySelectorAll('.cart__item--properties'), function (e) {
      return /ready.to.ship/i.test(e.textContent);
    }).length;
    if (inCart > shown) { document.dispatchEvent(new CustomEvent('cart:build')); }
  }

  document.addEventListener('bw:checkDates', function () {
    var nf = window.__bwNativeFetch || window.fetch;
    var start = Date.now();
    (function check() {
      nf('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); })
        .then(function (cart) {
          var ok = !cart.items.length || cart.items.every(function (i) { return i.properties && (i.properties.expected_ship_date || i.properties._expected_ship_date); });
          if (ok || Date.now() - start > 6000) {
            document.documentElement.classList.remove('bw-dates-pending');
            redrawDrawerIfBehind(cart);
          }
          else { setTimeout(check, 400); }
        })
        .catch(function () { document.documentElement.classList.remove('bw-dates-pending'); });
    })();
  });

})();
